import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ArrowDown, Sparkles, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

const HeroSection = () => {
  const { t, language } = useLanguage();
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullText = t('hero.title2');
  const rafRef = useRef(null);

  // Typing animation - using requestAnimationFrame for smoother performance
  useEffect(() => {
    setDisplayText('');
    setIsTyping(true);
    let index = 0;
    let lastTime = 0;
    const interval = 70;

    const animate = (time) => {
      if (time - lastTime >= interval) {
        if (index < fullText.length) {
          index++;
          setDisplayText(fullText.slice(0, index));
          lastTime = time;
        } else {
          setIsTyping(false);
          return;
        }
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [fullText, language]);

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-light-pink/5 to-mid-purple/5 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {/* Static gradient blobs - GPU composited */}
      <div className="absolute inset-0 pointer-events-none" style={{ contain: 'layout style paint' }}>
        <div className="absolute top-20 right-[15%] w-72 h-72 bg-light-pink/20 dark:bg-mid-purple/15 rounded-full blur-hero" />
        <div className="absolute bottom-20 left-[10%] w-80 h-80 bg-mid-blue/15 dark:bg-mid-blue/10 rounded-full blur-hero" />
      </div>

      {/* Floating dots - CSS only */}
      <div className="absolute inset-0 pointer-events-none" style={{ contain: 'layout style paint' }}>
        <div className="absolute top-[20%] right-[20%] w-3 h-3 bg-mid-purple rounded-full animate-float-gentle opacity-50" />
        <div className="absolute top-[55%] left-[12%] w-2 h-2 bg-mid-pink rounded-full animate-float-gentle delay-200 opacity-40" />
        <div className="absolute bottom-[30%] right-[28%] w-2 h-2 bg-mid-blue rounded-full animate-float-gentle delay-400 opacity-40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-black dark:text-white px-5 py-2.5 rounded-full mb-8 border border-mid-purple/20 dark:border-mid-purple/30 shadow-sm animate-fade-in-up">
            <Sparkles size={16} className="text-mid-purple" />
            <span className="text-sm font-medium">{t('hero.badge')}</span>
          </div>

          {/* Main Headline */}
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-black dark:text-white mb-6 animate-fade-in-up delay-100">
            {t('hero.title1')}
            <br />
            <span className="gradient-text">
              {displayText}
              {isTyping && <span className="animate-pulse">|</span>}
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-dark-grey dark:text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in-up delay-200">
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
            <Button
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-gray-100 text-white dark:text-black rounded-full px-8 py-6 text-sm font-mono uppercase tracking-wider group shadow-lg btn-lift"
            >
              <Zap size={18} className="mr-2" />
              {t('hero.cta')}
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-8 py-6 text-sm font-mono uppercase tracking-wider border-2 border-black/20 dark:border-white/20 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black btn-lift"
              onClick={() => scrollToSection('industries')}
            >
              {t('hero.ctaSecondary')}
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="mt-20 flex flex-wrap justify-center items-center gap-6 md:gap-10 animate-fade-in-up delay-400">
            {[
              { color: 'bg-mid-green', text: t('hero.trust1') },
              { color: 'bg-mid-purple', text: t('hero.trust2') },
              { color: 'bg-mid-blue', text: t('hero.trust3') }
            ].map((item) => (
              <div
                key={item.text}
                className="flex items-center gap-2 px-4 py-2 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-full border border-grey/20 dark:border-gray-700"
              >
                <div className={`w-2 h-2 rounded-full ${item.color}`} />
                <span className="text-sm font-medium text-black dark:text-white">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer animate-fade-in-up delay-500"
        onClick={() => scrollToSection('services')}
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs text-dark-grey dark:text-gray-500 uppercase tracking-wider">{t('hero.scroll')}</span>
          <ArrowDown size={20} className="text-mid-purple animate-bounce-arrow" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
